import axios from 'axios';
import logger from './logger';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// Helper to extract readable error message from API errors
// Handles Pydantic validation errors which come as arrays of objects
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') return detail;
  
  // If detail is an array of Pydantic validation errors
  if (Array.isArray(detail)) {
    // Try to extract meaningful messages
    const messages = detail
      .map(err => {
        if (typeof err === 'string') return err;
        if (err?.msg) return err.msg;
        return null;
      })
      .filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : fallback;
  }
  
  // If detail is an object with a message
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  
  return fallback;
};

const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silently ignore rate limit errors (429) — don't show scary toasts to users
    if (error.response?.status === 429) {
      logger.warn('Rate limit hit, request throttled');
      return Promise.reject(error);
    }
    // Log errors for debugging (production-safe)
    if (error.response) {
      logger.error(`API Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      logger.error('API No Response:', error.request);
    } else {
      logger.error('API Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  googleAuth: (data) => api.post('/auth/google', data),
  getGoogleOAuthUrl: () => api.get('/auth/google/url'),
};

export const examAPI = {
  getAll: () => api.get('/exams'),
  getById: (id) => api.get(`/exams/${id}`),
  getSubjects: (examId) => api.get(`/exams/${examId}/subjects`),
  getInfo: (examId) => api.get(`/exams/${examId}/info`),
  getAIInfo: (examName) => api.get(`/ai/exam-info/${encodeURIComponent(examName)}`),
  getDifficultyProfile: (examName) => api.get(`/ai/difficulty-profile/${encodeURIComponent(examName)}`),
  getAllDifficultyProfiles: () => api.get('/ai/all-difficulty-profiles'),
};

export const examInfoAPI = {
  getByExamId: (examId) => api.get(`/exams/${examId}/info`),
};

export const subjectAPI = {
  getById: (id) => api.get(`/subjects/${id}`),
  getChapters: (subjectId) => api.get(`/subjects/${subjectId}/chapters`),
};

export const chapterAPI = {
  getById: (id) => api.get(`/chapters/${id}`),
};

export const questionAPI = {
  getAll: (params) => api.get('/questions', { params }),
  getById: (id) => api.get(`/questions/${id}`),
  getComments: (questionId) => api.get(`/questions/${questionId}/comments`),
  addComment: (questionId, content) => api.post(`/questions/${questionId}/comments`, { content }),
};

export const testAPI = {
  generate: (data) => api.post('/tests/generate', data),
  getById: (id) => api.get(`/tests/${id}`),
  submit: (testId, answers, totalTimeSeconds = null, sessionData = null) => 
    api.post(`/tests/${testId}/submit`, { 
      answers, 
      total_time_seconds: totalTimeSeconds,
      session_data: sessionData 
    }),
  getHistory: () => api.get('/tests/history', { params: { limit: 100 } }),
};

export const mistakeAPI = {
  getAll: () => api.get('/mistakes'),
  generateTest: (numQuestions) => api.post('/mistakes/generate-test', null, { params: { num_questions: numQuestions } }),
};

export const bookmarkAPI = {
  getAll: () => api.get('/bookmarks'),
  add: (questionId, note = null) => api.post(`/bookmarks/${questionId}`, { note }),
  remove: (questionId) => api.delete(`/bookmarks/${questionId}`),
  check: (questionId) => api.get(`/bookmarks/check/${questionId}`),
  generateTest: (numQuestions = null) => api.post('/bookmarks/generate-test', null, { params: numQuestions ? { num_questions: numQuestions } : {} }),
};

export const dailyChallengeAPI = {
  get: () => api.get('/daily-challenge'),
  complete: (score) => api.post('/daily-challenge/complete', null, { params: { score } }),
};

export const studyRoomAPI = {
  create: (data) => api.post('/study-rooms', data),
  getAll: (activeOnly = true) => api.get('/study-rooms', { params: { active_only: activeOnly } }),
  join: (roomId) => api.post(`/study-rooms/${roomId}/join`),
  getById: (roomId) => api.get(`/study-rooms/${roomId}`),
};

export const challengeAPI = {
  create: (data) => api.post('/challenges', data),
  getAll: () => api.get('/challenges'),
  getByCode: (code) => api.get(`/challenges/${code}`),
  join: (code) => api.post(`/challenges/${code}/join`),
  start: (code) => api.post(`/challenges/${code}/start`),
  activate: (code) => api.post(`/challenges/${code}/activate`),
  cancel: (code) => api.delete(`/challenges/${code}`),
  delete: (id) => api.delete(`/challenges/id/${id}`),
  submit: (code, data) => api.post(`/challenges/${code}/submit`, data),
};

export const aiAPI = {
  generatePlan: (data) => api.post('/ai/generate-plan', data),
  chat: (message) => api.post('/ai/chat', { message }),
  getPlan: () => api.get('/ai/plan'),
};

export const leaderboardAPI = {
  get: (limit = 50) => api.get('/leaderboard', { params: { limit } }),
};

export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
};

export default api;
